import React, { useState, useEffect, useRef } from 'react';
import { getGalleryImages, uploadGalleryImage, deleteGalleryImage, updateGalleryImage } from '../services/pocketbase';
import { FiUpload, FiTrash2, FiPlus, FiX, FiMove, FiSave, FiRefreshCw } from 'react-icons/fi';
import './AdminGallery.css';

function AdminGallery() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    // Upload state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const [dragActive, setDragActive] = useState(false);

    // Reorder state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [isSorting, setIsSorting] = useState(false);
    const [orderChanged, setOrderChanged] = useState(false);

    useEffect(() => {
        loadImages();
    }, []);

    const loadImages = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getGalleryImages();
            setImages(data);
        } catch (error) {
            console.error(error);
            if (error.status === 404) {
                setError("La collection 'gallery' n'existe pas encore dans PocketBase.");
            } else {
                setError("Impossible de charger la galerie.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation(); // Évite d'ouvrir l'image si on clique sur suppr
        if (window.confirm('Voulez-vous vraiment supprimer cette image ?')) {
            try {
                await deleteGalleryImage(id);
                setImages(images.filter(img => img.id !== id));
            } catch (error) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) return;

        try {
            // On calcule le prochain ordre (max + 1)
            const maxOrder = images.reduce((max, img) => (img.order > max ? img.order : max), 0);

            const result = await uploadGalleryImage({
                title: uploadTitle,
                image: uploadFile,
                order: maxOrder + 1
            });
            setImages([...images, result]);
            setUploadFile(null);
            setUploadTitle('');
            setIsUploading(false);
        } catch (error) {
            alert('Erreur lors de l\'upload');
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setUploadFile(e.dataTransfer.files[0]);
            setIsUploading(true);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setUploadFile(e.target.files[0]);
            setIsUploading(true);
        }
    };

    // --- SORTING LOGIC ---

    const handleSortStart = (e, index) => {
        dragItem.current = index;
        e.target.closest('.gallery-item').classList.add('dragging');
    };

    const handleSortEnter = (e, index) => {
        dragOverItem.current = index;

        // Optimistic UI update
        const newImages = [...images];
        const draggedImage = newImages[dragItem.current];
        newImages.splice(dragItem.current, 1);
        newImages.splice(dragOverItem.current, 0, draggedImage);

        dragItem.current = dragOverItem.current;
        setImages(newImages);
        setOrderChanged(true);
    };

    const handleSortEnd = (e) => {
        e.target.closest('.gallery-item').classList.remove('dragging');
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const saveOrder = async () => {
        setIsSorting(true);
        try {
            // Mettre à jour l'ordre de chaque image modifiée sur le serveur
            // Pour ne pas spammer, on met tout à jour.
            // On utilise Promise.all pour paralléliser
            const promises = images.map((img, index) => {
                // On met à jour seulement si l'ordre est différent (optimisation optionnelle)
                return updateGalleryImage(img.id, { order: index + 1 });
            });
            await Promise.all(promises);
            setOrderChanged(false);
            alert("Ordre sauvegardé !");
        } catch (error) {
            console.error(error);
            alert("Erreur lors de la sauvegarde de l'ordre. Vérifiez que le champ 'order' existe dans PocketBase.");
        } finally {
            setIsSorting(false);
        }
    };

    const getImageUrl = (record) => {
        return `${process.env.REACT_APP_POCKETBASE_URL || 'https://studioambre.infocolab.online'}/api/files/gallery/${record.id}/${record.image}`;
    };

    if (loading) return <div className="loader">Chargement de la galerie...</div>;

    if (error) {
        return (
            <div className="admin-gallery">
                <div className="admin-error-container">
                    <h3>⚠️ Configuration Requise</h3>
                    <p>{error}</p>
                    <div className="setup-instructions">
                        <p>Pour activer la galerie dynamique, créez une collection dans PocketBase avec ces paramètres :</p>
                        <ul>
                            <li><strong>Nom :</strong> <code>gallery</code></li>
                            <li><strong>Type :</strong> Base</li>
                            <li><strong>Champs :</strong>
                                <ul>
                                    <li><code>title</code> (Texte)</li>
                                    <li><code>image</code> (Fichier)</li>
                                    <li><code>order</code> (Nombre) <span className="badge-new">Requis pour le tri</span></li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <button className="btn-retry" onClick={loadImages}>Réessayer</button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-gallery">
            <div className="admin-header">
                <h2>📸 Gestion de la Galerie</h2>
                <div className="header-actions">
                    {isSorting && <span className="saving-indicator"><FiRefreshCw className="spin" /> Sauvegarde...</span>}
                    <button className="btn-add" onClick={() => setIsUploading(!isUploading)}>
                        {isUploading ? <FiX /> : <FiPlus />} {isUploading ? 'Annuler' : 'Ajouter'}
                    </button>
                </div>
            </div>

            {isUploading && (
                <div className="upload-section">
                    <form onSubmit={handleUpload} className="upload-form">
                        <div
                            className={`drop-zone ${dragActive ? 'active' : ''} ${uploadFile ? 'has-file' : ''}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('file-upload').click()}
                        >
                            <input
                                type="file"
                                id="file-upload"
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            {uploadFile ? (
                                <div className="file-preview">
                                    <p>Fichier : {uploadFile.name}</p>
                                    <p className="change-file-text">Cliquer pour changer</p>
                                </div>
                            ) : (
                                <div className="drop-zone-content">
                                    <FiUpload size={30} />
                                    <p>Glissez une image ici ou cliquez pour parcourir</p>
                                </div>
                            )}
                        </div>

                        <div className="upload-details">
                            <input
                                type="text"
                                placeholder="Titre / Description (optionnel)"
                                value={uploadTitle}
                                onChange={e => setUploadTitle(e.target.value)}
                            />
                            <button type="submit" className="btn-upload" disabled={!uploadFile}>
                                Uploader
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="gallery-grid">
                {images.length === 0 ? (
                    <div className="no-images">Aucune image dans la galerie</div>
                ) : (
                    images.map((img, index) => (
                        <div
                            key={img.id}
                            className="gallery-item draggable"
                            draggable
                            onDragStart={(e) => handleSortStart(e, index)}
                            onDragEnter={(e) => handleSortEnter(e, index)}
                            onDragEnd={handleSortEnd}
                            onDragOver={(e) => e.preventDefault()}
                        >
                            <div className="drag-handle">
                                <FiMove />
                            </div>
                            <img src={getImageUrl(img)} alt={img.title || 'Galerie'} loading="lazy" />
                            <div className="gallery-overlay">
                                <p>{img.title}</p>
                                <button onClick={(e) => handleDelete(img.id, e)} className="btn-delete-img">
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

        </div>
    );
}

export default AdminGallery;
